//#region IMPORT
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import { Business, Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import SelectRepresentativeModal from './SelectRepresentativeModal';
import DatePickerBasic from '../../../../Components/MUIs/DatePickerBasic';
//#endregion

const today = dayjs().format('YYYY-MM-DD');
const oneYearLater = dayjs().add(1, 'year').format('YYYY-MM-DD');

const MEMBER_INPUTS = {
  depNm1: '',
  depNm2: '',
  depNm3: '',
  depNm4: '',
  authNm: '근로자',
  tbmYn: 'N',
  approvalYn: 'N',
  position: '사업주',
  rank: '',
  name: '',
  id: '',
  tel: '',
  birth: '',
  bloodType: '',
  address: '',
  nation: '대한민국',
};

const initialInputs = {
  no: '',
  coCd: '',
  coNm: '',
  coId: '',
  name: '',
  address1: '',
  address2: '',
  contractStartDate: today,
  contractEndDate: oneYearLater,
  contractMemberCount: 0,
  monthlyContractFee: '',
  paymentDay: '',
};

const MODAL_PRIMARY = '#137fec';

const formCardSx = {
  border: '1px solid',
  borderColor: 'rgba(226, 232, 240, 1)',
  borderRadius: 2,
  overflow: 'hidden',
  boxShadow:
    '0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.04)',
};

const labelHeadSx = {
  py: 1.75,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)',
};

const inputCellSx = { p: 1.75, bgcolor: '#fff' };

function FieldLabel({ children, required }) {
  return (
    <Typography
      component="div"
      sx={{
        fontSize: '0.8125rem',
        fontWeight: 600,
        textAlign: 'center',
        color: 'text.secondary',
        letterSpacing: '0.02em',
        lineHeight: 1.4,
      }}
    >
      {required ? (
        <>
          <Box
            component="span"
            sx={{ color: 'error.main', mr: 0.35, fontWeight: 700 }}
          >
            *
          </Box>
          {children}
        </>
      ) : (
        children
      )}
    </Typography>
  );
}

function AddCompanyModal({
  open,
  onClose,
  onSave,
  userInfo,
  memInputs,
  setMemInputs,
  editMode = false,
  editCompanyData = null,
}) {
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [representativeModalOpen, setRepresentativeModalOpen] = useState(false);

  // 날짜 형식 변환 함수: "06/26/2025 00:00:00" -> "YYYY-MM-DD"
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';

    // "MM/DD/YYYY HH:mm:ss" 형식 파싱
    const match = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) {
      const [, month, day, year] = match;
      return `${year}-${month}-${day}`;
    }

    // 이미 "YYYY-MM-DD" 형식이면 그대로 반환
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateStr;
    }

    // dayjs로 파싱 시도
    const parsed = dayjs(dateStr);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }

    return '';
  };

  useEffect(() => {
    if (open) {
      if (editMode && editCompanyData?.detailData?.companyInfo) {
        // 수정 모드: companyInfo에서 데이터 가져오기
        const companyInfo = editCompanyData.detailData.companyInfo;
        const companyData = editCompanyData.companyData || {};
        console.log('[AddCompanyModal] companyInfo', companyInfo);
        console.log('[AddCompanyModal] companyData', companyData);

        setInputs({
          no: companyData.no || '',
          coCd: companyData.coCd || companyInfo.id || '',
          coNm: companyInfo.companyName || '',
          coId: companyInfo.id || '',
          name: companyInfo.name || '',
          // 주소: address1에 주소, address2에 상세주소
          address1:
            companyInfo.address1 ||
            companyInfo.address ||
            companyData.address1 ||
            '',
          address2: companyInfo.address2 || companyData.address2 || '',
          // 날짜 형식 변환 (companyInfo 우선, 없으면 companyData에서)
          contractStartDate: formatDateForInput(
            companyInfo.contractStartDate || companyData.contractStartDate || ''
          ),
          contractEndDate: formatDateForInput(
            companyInfo.contractEndDate || companyData.contractEndDate || ''
          ),
          contractMemberCount: companyInfo.totalWorkerCount || 0,
          monthlyContractFee:
            companyInfo.monthlyContractFee ??
            companyData.monthlyContractFee ??
            '',
          paymentDay: (() => {
            const pd =
              companyInfo.paymentDay ??
              companyData.paymentDay ??
              companyInfo.PaymentDay ??
              companyData.PaymentDay;
            if (pd !== undefined && pd !== null && String(pd).trim() !== '') {
              const n = Math.min(31, Math.max(1, Number(String(pd).trim())));
              if (Number.isFinite(n)) return n;
            }
            const pDate =
              companyInfo.paymentDate ||
              companyData.paymentDate ||
              companyInfo.PaymentDate ||
              companyData.PaymentDate ||
              '';
            if (pDate) {
              const fs = formatDateForInput(pDate);
              if (fs) {
                const d = dayjs(fs);
                if (d.isValid()) return d.date();
              }
              const d = dayjs(pDate);
              if (d.isValid()) return d.date();
            }
            return '';
          })(),
        });
        // memInputs도 설정
        // companyData의 Id 값 확인 (id 또는 Id 필드)
        const companyDataId = companyData.id || companyData.Id || '';

        setMemInputs({
          ...MEMBER_INPUTS,
          name: companyInfo.name || '',
          // 대표자 Id: companyInfo.id 우선, 없으면 companyData의 Id 사용
          id: companyInfo.id || companyDataId || '',
          position: companyInfo.position || '',
          rank: companyInfo.rank || '',
          tel: companyInfo.tel || '',
          depNm1: companyInfo.depNm1 || '',
          depNm2: companyInfo.depNm2 || '',
          depNm3: companyInfo.depNm3 || '',
          depNm4: companyInfo.depNm4 || '',
          tbmYn: companyInfo.tbmYn || 'N',
          approvalYn: companyInfo.approvalYn || 'N',
          nation: companyInfo.nation || '대한민국',
          bloodType: companyInfo.bloodType || '',
          birth: companyInfo.birth || '',
          address: companyInfo.address || '',
        });
      } else {
        // 신규 모드
        setInputs(initialInputs);
        setMemInputs(MEMBER_INPUTS);
      }
      setErrors({});
    }
  }, [open, editMode, editCompanyData]);

  const handleChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
      newYn: 'Y',
    }));
    // 에러 초기화
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (userInfo?.authCd === 'ADMIN') {
      if (!inputs.coNm || !inputs.coNm.trim()) {
        newErrors.coNm = '회사명은 필수입니다.';
      }
      const feeNum = Number(inputs.monthlyContractFee);
      if (
        inputs.monthlyContractFee === '' ||
        inputs.monthlyContractFee === null ||
        inputs.monthlyContractFee === undefined ||
        Number.isNaN(feeNum) ||
        feeNum < 0
      ) {
        newErrors.monthlyContractFee = '계약금(월)을 올바르게 입력해 주세요.';
      }
      const dayNum = Number(inputs.paymentDay);
      if (
        inputs.paymentDay === '' ||
        inputs.paymentDay === null ||
        inputs.paymentDay === undefined ||
        !Number.isFinite(dayNum) ||
        dayNum < 1 ||
        dayNum > 31
      ) {
        newErrors.paymentDay = '결제일(매월)은 1~31 사이로 입력해 주세요.';
      }
      if (!inputs.name || !inputs.name.trim()) {
        newErrors.coId = '대표자는 필수입니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const rowData = {
      ...inputs,
      monthlyContractFee:
        inputs.monthlyContractFee === '' || inputs.monthlyContractFee === null
          ? ''
          : Number(inputs.monthlyContractFee),
      paymentDay:
        inputs.paymentDay === '' || inputs.paymentDay === null
          ? ''
          : Math.min(31, Math.max(1, Number(inputs.paymentDay))),
      no: inputs.no || (editMode ? inputs.coCd : `temp_${Date.now()}`),
      newYn: editMode ? 'N' : 'Y',
    };

    onSave([rowData]);
  };

  const isAdmin = userInfo?.authCd === 'ADMIN';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="body"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(3px)',
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.16)',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(125deg, rgba(19,127,236,0.09) 0%, #fff 42%, #fff 100%)`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                flexShrink: 0,
                bgcolor: 'rgba(19,127,236,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: MODAL_PRIMARY,
              }}
            >
              <Business sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.3,
                }}
              >
                {editMode ? '계약 수정' : '새 계약 추가'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.35 }}
              >
                회사·계약 정보를 입력한 뒤 저장합니다
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              bgcolor: 'rgba(15,23,42,0.04)',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'rgba(15,23,42,0.08)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.5,
            mt: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 22,
              borderRadius: 1,
              bgcolor: MODAL_PRIMARY,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            회사정보
          </Typography>
          {isAdmin && (
            <Chip
              label="관리자 입력"
              size="small"
              sx={{
                height: 24,
                fontSize: '0.72rem',
                fontWeight: 600,
                bgcolor: 'rgba(19,127,236,0.1)',
                color: MODAL_PRIMARY,
                border: '1px solid rgba(19,127,236,0.22)',
              }}
            />
          )}
        </Box>

        <Box sx={formCardSx}>
          {/* 첫 번째 행: 회사명, 대표자 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(226, 232, 240, 1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
                borderBottom: {
                  xs: '1px solid rgba(226, 232, 240, 1)',
                  md: 'none',
                },
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel required={isAdmin}>회사명</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.coNm || ''}
                  onChange={(e) => handleChange('coNm', e.target.value)}
                  error={!!errors.coNm}
                  helperText={errors.coNm}
                  disabled={!isAdmin}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel required={isAdmin}>대표자</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  value={
                    inputs.coId
                      ? `${inputs.name || ''} / ${inputs.coId}`
                      : inputs.name || ''
                  }
                  placeholder="클릭하여 대표자 선택"
                  error={!!errors.coId}
                  helperText={errors.coId}
                  disabled={!isAdmin}
                  onClick={() => {
                    if (isAdmin) {
                      setRepresentativeModalOpen(true);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      cursor: isAdmin ? 'pointer' : 'default',
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 두 번째 행: 주소, 상세주소 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(226, 232, 240, 1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
                borderBottom: {
                  xs: '1px solid rgba(226, 232, 240, 1)',
                  md: 'none',
                },
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel>주소</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.address1 || ''}
                  onChange={(e) => handleChange('address1', e.target.value)}
                  disabled={!isAdmin}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel>상세주소</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.address2 || ''}
                  onChange={(e) => handleChange('address2', e.target.value)}
                  disabled={!isAdmin}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 세 번째 행: 계약시작일자, 계약종료일자 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(226, 232, 240, 1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
                borderBottom: {
                  xs: '1px solid rgba(226, 232, 240, 1)',
                  md: 'none',
                },
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel>계약시작일자</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <DatePickerBasic
                  value={
                    inputs.contractStartDate
                      ? dayjs(inputs.contractStartDate)
                      : dayjs()
                  }
                  onChange={(date) => {
                    if (date) {
                      handleChange(
                        'contractStartDate',
                        dayjs(date).format('YYYY-MM-DD')
                      );
                    }
                  }}
                  disabled={!isAdmin}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel>계약종료일자</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <DatePickerBasic
                  value={
                    inputs.contractEndDate
                      ? dayjs(inputs.contractEndDate)
                      : dayjs()
                  }
                  onChange={(date) => {
                    if (date) {
                      handleChange(
                        'contractEndDate',
                        dayjs(date).format('YYYY-MM-DD')
                      );
                    }
                  }}
                  disabled={!isAdmin}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 네 번째 행: 총 계약 인원 수, 계약금(월) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(226, 232, 240, 1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
                borderBottom: {
                  xs: '1px solid rgba(226, 232, 240, 1)',
                  md: 'none',
                },
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel>총 계약 인원 수</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={inputs.contractMemberCount || ''}
                  onChange={(e) =>
                    handleChange('contractMemberCount', Number(e.target.value))
                  }
                  disabled={!isAdmin}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel required={isAdmin}>계약금(월)</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={
                    inputs.monthlyContractFee === '' ||
                    inputs.monthlyContractFee === null ||
                    inputs.monthlyContractFee === undefined
                      ? ''
                      : inputs.monthlyContractFee
                  }
                  onChange={(e) =>
                    handleChange(
                      'monthlyContractFee',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  error={!!errors.monthlyContractFee}
                  helperText={
                    errors.monthlyContractFee ||
                    '월 단위 금액(원)을 입력하세요.'
                  }
                  disabled={!isAdmin}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 다섯 번째 행: 결제일 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(226, 232, 240, 1)' },
                borderBottom: {
                  xs: '1px solid rgba(226, 232, 240, 1)',
                  md: 'none',
                },
              }}
            >
              <Box sx={labelHeadSx}>
                <FieldLabel required={isAdmin}>결제일(매월)</FieldLabel>
              </Box>
              <Box sx={inputCellSx}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  disabled={!isAdmin}
                  value={
                    inputs.paymentDay === '' || inputs.paymentDay === null
                      ? ''
                      : inputs.paymentDay
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      handleChange('paymentDay', '');
                      return;
                    }
                    const n = Math.min(31, Math.max(1, Number(v)));
                    if (Number.isFinite(n)) handleChange('paymentDay', n);
                  }}
                  inputProps={{ min: 1, max: 31 }}
                  placeholder="1~31"
                  error={!!errors.paymentDay}
                  helperText={errors.paymentDay || '매월 결제일(일)'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: !isAdmin ? 'grey.50' : 'background.paper',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: '120px 1fr',
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.25,
          bgcolor: 'rgba(248, 250, 252, 0.85)',
          borderTop: '1px solid rgba(226, 232, 240, 1)',
          gap: 1.25,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="medium"
          sx={{
            minWidth: 96,
            borderRadius: 1.5,
            borderColor: 'rgba(203, 213, 225, 1)',
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              borderColor: 'rgba(148, 163, 184, 1)',
              bgcolor: 'rgba(248, 250, 252, 1)',
            },
          }}
        >
          취소
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="medium"
          sx={{
            minWidth: 112,
            borderRadius: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: MODAL_PRIMARY,
            boxShadow: '0 2px 8px rgba(19, 127, 236, 0.35)',
            '&:hover': {
              bgcolor: '#0f6dd1',
              boxShadow: '0 4px 14px rgba(19, 127, 236, 0.4)',
            },
          }}
        >
          저장하기
        </Button>
      </DialogActions>

      {/* 대표자 선택 모달 */}
      <SelectRepresentativeModal
        open={representativeModalOpen}
        onClose={() => setRepresentativeModalOpen(false)}
        onSelect={(selected) => {
          handleChange('coId', selected.coId);
          handleChange('name', selected.name);
          if (selected.memberData) {
            setMemInputs((prev) => ({ ...prev, ...selected.memberData }));
          }
        }}
        userInfo={userInfo}
        setInputs={setMemInputs}
        inputs={memInputs}
        companyInfo={
          editMode && editCompanyData?.detailData?.companyInfo
            ? editCompanyData.detailData.companyInfo
            : null
        }
      />
    </Dialog>
  );
}

export default AddCompanyModal;
